import type { EventBuilder, ExceptionlessClient } from "@exceptionless/react";
import * as TraceKit from "tracekit";
import type { IMasterStores } from "../../app/MasterStores";
import type { IKnownExceptionsToIgnoreCatcher } from "./KnownExceptionsToIgnoreCatcher";
import type { ISessionStore } from "../../shared-components/session/SessionStore";

export class DiagnosticProperty {
	constructor(
		public key: string,
		public value: any,
	) {}
}

export interface ILogger {
	logException(error: Error, addContext?: boolean, properties?: DiagnosticProperty[], tags?: string[]): void;
}

export class Logger implements ILogger {
	constructor(private readonly exceptionlessStore: IExceptionlessStore) {}

	logException(error: Error, addContext?: boolean, properties?: DiagnosticProperty[], tags?: string[]) {
		this.exceptionlessStore.logException(error, addContext, properties, tags);
	}
}

export type UnhandledExceptionEventHandler = (stackTrace: TraceKit.StackTrace) => void;

export interface IExceptionlessStore extends ILogger {
	logException(error: Error, addContext?: boolean, properties?: DiagnosticProperty[], tags?: string[]): void;

	addUnhandledExceptionListener(handler: UnhandledExceptionEventHandler): void;
	removeUnhandledExceptionListener(handler: UnhandledExceptionEventHandler): void;
}

export class ExceptionlessStore implements IExceptionlessStore {
	private readonly _unhandledExceptionListeners: UnhandledExceptionEventHandler[];

	constructor(
		public readonly client: ExceptionlessClient | null,
		public readonly knownExceptionsToIgnoreCatcher: IKnownExceptionsToIgnoreCatcher,
	) {
		console.log("🔥 registering global onerror handler");

		this._unhandledExceptionListeners = [];
		//this.client.config.useDebugLogger();

		if (client) {
			TraceKit.report.subscribe((stackTrace, options) => this.processUnhandledException(client, stackTrace, options));
		}
		TraceKit.extendToAsynchronousCallbacks();

		console.log("🔥 global onerror handler configured");
	}

	processUnhandledException(client: ExceptionlessClient, stackTrace: TraceKit.StackTrace, options?: any): void {
		const { knownExceptionsToIgnoreCatcher } = this;

		const exception = new Error(stackTrace.message ?? options?.status ?? "Script error");

		if (knownExceptionsToIgnoreCatcher.shouldIgnoreException(exception)) return;

		this.processUnhandledExceptionEventHandlers(stackTrace);

		try {
			const builder = client.createUnhandledException(exception, "onerror");
			builder.setContextProperty("@@_TraceKit.StackTrace", stackTrace);

			this.addContext(builder);
			builder.submit();
		} catch (e) {
			console.error("Could not post unhandled exception to exceptionless", e);
		}
	}

	logException(error: Error, addContext?: boolean, properties?: DiagnosticProperty[], tags?: string[]) {
		const { knownExceptionsToIgnoreCatcher } = this;

		if (knownExceptionsToIgnoreCatcher.shouldIgnoreException(error)) return;

		if (!this.client) {
			return;
		}

		try {
			const eventBuilder = this.client.createException(error);

			if (properties) {
				for (const property of properties) {
					eventBuilder.setProperty(property.key, property.value);
				}
			}

			if (tags) {
				if (tags.length > 0) eventBuilder.addTags(...tags);
			}

			if (addContext) {
				this.addContext(eventBuilder);
			}

			eventBuilder.submit();
		} catch (e) {
			console.error("Could not post unhandled exception to exceptionless", e);
		}
	}

	addContext(eventBuilder: EventBuilder) {
		// this is an abstract method that is ment to be overridden
	}

	addUnhandledExceptionListener(handler: UnhandledExceptionEventHandler): void {
		this._unhandledExceptionListeners.push(handler);
	}

	removeUnhandledExceptionListener(handler: UnhandledExceptionEventHandler): void {
		const { _unhandledExceptionListeners } = this;
		const index = _unhandledExceptionListeners.indexOf(handler);
		if (index < 0) return;

		_unhandledExceptionListeners.splice(index, 1);
	}

	private processUnhandledExceptionEventHandlers(stackTrace: TraceKit.StackTrace) {
		const { _unhandledExceptionListeners } = this;

		for (const unhandledExceptionListener of _unhandledExceptionListeners) {
			try {
				unhandledExceptionListener(stackTrace);
			} catch (e) {
				console.warn(`⚠ An error occured inside one of the unhandledExceptionHandlers: ${e.message}`, e);
			}
		}
	}
}

export class ViewerExceptionlessStore extends ExceptionlessStore {
	private _masterStores: IMasterStores | null;

	constructor(
		public readonly client: ExceptionlessClient | null,
		public readonly knownExceptionsToIgnoreCatcher: IKnownExceptionsToIgnoreCatcher,
	) {
		super(client, knownExceptionsToIgnoreCatcher);
		this._masterStores = null;
	}

	setMasterStores(masterStores: IMasterStores) {
		if (!masterStores) {
			console.warn("masterStores were already set for the ExceptionlessStore. Ignoring this call");
			return;
		}
		this._masterStores = masterStores;
	}

	addContext(eventBuilder: EventBuilder) {
		const { _masterStores } = this;
		if (!_masterStores) {
			console.warn("Trying to add context to an Exceptionless event, but there's no masterStores registered yet!!");
			return;
		}

		try {
			const { monitorStore, monitorWindowsStore, sessionStore, environmentStore, tabsStore } = _masterStores;
			const { monitors } = monitorStore;
			const { all } = monitorWindowsStore;

			if (monitors)
				eventBuilder.setProperty(
					"Monitors",
					monitors.map(monitor => monitor.diagnosticProperties()),
				);

			if (all) {
				eventBuilder.setProperty(
					"MonitorWindows",
					all.map(mw => mw.diagnosticProperties()),
				);
			}

			this.setUserInfo(sessionStore, eventBuilder);

			if (environmentStore) {
				const { currentEnvironment } = environmentStore;
				eventBuilder.setProperty("environment", {
					baseUrl: currentEnvironment?.url,
				});
			}

			if (tabsStore) {
				const { activeTab } = tabsStore;
				if (activeTab) {
					eventBuilder.setProperty("ActiveTab", activeTab.kind);

					const properties = activeTab.diagnosticProperties();

					for (const property of properties) {
						eventBuilder.setProperty(property.key, property.value);
					}
				}
			}
		} catch (e) {
			console.error("Could not add context to the exceptionless call", e);
		}
	}

	private setUserInfo(sessionStore: ISessionStore, eventBuilder: EventBuilder) {
		if (!sessionStore) {
			return;
		}

		const { currentUser, currentAnonymousUser } = sessionStore;

		if (currentUser) {
			const userInfo = {
				id: currentUser.id,
				email: currentUser.email,
				firstName: currentUser.firstName,
				lastName: currentUser.lastName,
				isGlobalAdmin: currentUser.isGlobalAdmin,
				isStrongAuthenticated: currentUser.isStrongAuthenticated,
				ipAddress: currentUser.ipAddress,
				settings: {
					generalUserSettings: currentUser.settings.generalUserSettings,
				},
			};

			eventBuilder.setProperty("userInfo", userInfo);
		} else if (currentAnonymousUser) {
			let anonymousUserInfo: object = {
				id: currentAnonymousUser.id,
				ipAddress: currentAnonymousUser.ipAddress,
				isStrongAuthenticated: currentAnonymousUser.isStrongAuthenticated,
				loginMethod: currentAnonymousUser.loginMethod,
			};

			const {
				anonymousUserWithBankId,
				anonymousUserWithCozoToken,
				anonymousUserWithEmrToken,
				anonymousUserWithReferenceNumber,
				anonymousUserWithRswToken,
				anonymousUserWithSharedBucket,
			} = currentAnonymousUser;

			if (anonymousUserWithBankId) {
				const { sourceId, patientNationalNumberType, patientNationalNumber, bankIdSessionIdentification } = anonymousUserWithBankId;
				anonymousUserInfo = {
					...anonymousUserInfo,
					sourceId,
					patientNationalNumberType,
					patientNationalNumber,
					bankIdSessionIdentification,
				};
			}
			if (anonymousUserWithCozoToken) {
				const { sourceId, firstName, lastName, site, studyId, hcPartyTyp, cozoGuid } = anonymousUserWithCozoToken;
				anonymousUserInfo = {
					...anonymousUserInfo,
					sourceId,
					firstName,
					lastName,
					site,
					studyId,
					hcPartyTyp,
					cozoGuid,
				};
			}
			if (anonymousUserWithEmrToken) {
				const { sourceId, patientNationalNumber, accessionNumber, patientId, impersonatedUserId, impersonatedUserName, settings } = anonymousUserWithEmrToken;
				anonymousUserInfo = {
					...anonymousUserInfo,
					sourceId,
					patientNationalNumber,
					accessionNumber,
					patientId,
					impersonatedUserId,
					impersonatedUserName,
					settings: {
						generalUserSettings: settings?.generalUserSettings,
					},
				};
			}
			if (anonymousUserWithReferenceNumber) {
				const { sourceId, referenceNumber, birthDate } = anonymousUserWithReferenceNumber;
				anonymousUserInfo = {
					...anonymousUserInfo,
					sourceId,
					referenceNumber,
					birthDate,
				};
			}
			if (anonymousUserWithRswToken) {
				const { sourceId, firstName, lastName, site, studyId, hcPartyType } = anonymousUserWithRswToken;
				anonymousUserInfo = {
					...anonymousUserInfo,
					sourceId,
					firstName,
					lastName,
					site,
					studyId,
					hcPartyType,
				};
			}
			if (anonymousUserWithSharedBucket) {
				const { birthDate, sharedBucketCode } = anonymousUserWithSharedBucket;
				anonymousUserInfo = {
					...anonymousUserInfo,
					birthDate,
					sharedBucketCode,
				};
			}

			eventBuilder.setProperty("anonymousUserInfo", anonymousUserInfo);
		}
	}
}
